// Buffers coarse interview debug-log entries (see log.store.js's shouldShipLogEntry/
// sanitizeLogPayload) and ships them to the backend in batches. Always-on (not gated by
// devMode) — this is the persisted, admin-investigable trail, independent of LogStore's
// dev-only in-memory ring buffer. Modeled on interviewApi.js's plain-class-wrapper pattern.
import { retryable, withTimeout } from "./interviewApi";
import { SHIP_INTERVIEW_LOGS_GQL } from "../gql/interviewLog.mutations";
import { shouldShipLogEntry, sanitizeLogPayload } from "../stores/log.store";

/**
 * @param {{ apolloClient: import('@apollo/client').ApolloClient<any>, maxBatchSize?: number }} deps
 */
export function createInterviewLogShipper({ apolloClient, maxBatchSize = 50 }) {
  const client = apolloClient;
  let queue = [];
  let disposed = false;

  /**
   * @param {{ tag: string, payload?: any, level?: string, ts: number }} entry
   */
  function enqueue(entry) {
    if (disposed || !entry || !shouldShipLogEntry(entry.tag, entry.payload)) return;
    queue.push({
      tag: entry.tag,
      payload: JSON.stringify(sanitizeLogPayload(entry.payload) ?? null),
      level: entry.level || "info",
      ts: entry.ts,
    });
  }

  async function sendBatch(entries, { recordingId, sectionId, callId }) {
    return retryable(async (attempt, controller) => {
      await withTimeout(
        client.mutate({
          mutation: SHIP_INTERVIEW_LOGS_GQL,
          variables: { recordingId, sectionId, callId, entries },
          context: { fetchOptions: { signal: controller.signal } },
        }),
        controller,
        8000
      );
    }, { maxAttempts: 2 });
  }

  /**
   * Best-effort flush. Never throws — log shipping must not block interview UX.
   * @param {{ recordingId: string, sectionId?: string, callId?: string }} ctx
   */
  async function flush({ recordingId, sectionId, callId } = {}) {
    if (!recordingId || !queue.length) return;
    const batch = queue;
    queue = [];
    for (let i = 0; i < batch.length; i += maxBatchSize) {
      const chunk = batch.slice(i, i + maxBatchSize);
      try {
        await sendBatch(chunk, { recordingId, sectionId, callId });
      } catch (e) {
        console.warn("[interviewLogShipper] failed to ship log batch", e?.message || e);
      }
    }
  }

  function dispose() {
    disposed = true;
    queue = [];
  }

  return { enqueue, flush, dispose };
}
